import {
  ItemJobCustomerPortal,
  LocationCustomerPortal,
  MaterialCustomerPortal,
  RoomJobCustomerPortal,
} from "../models/customer.portal";
import { Destination } from "../models/destination";
import { JobMode } from "../models/job.mode";
import { Measurement } from "../models/measurement";
import { handleGetConvertValue } from "./converts";
import { DESTINATION } from "./enums";

export const getAddressDestinationItems = (
  inventory: RoomJobCustomerPortal[],
  destinationAddresses?: LocationCustomerPortal[]
) => {
  const destinationsId = destinationAddresses?.map((address) => address.id);
  const destinationItems: { [x: string]: ItemJobCustomerPortal[] } = {};

  destinationsId?.forEach((destinationId) => {
    if (destinationId) {
      const items: ItemJobCustomerPortal[] = [];

      inventory?.forEach((inventoryValue) => {
        inventoryValue?.itemsJob?.forEach((itemJob) => {
          if (
            itemJob &&
            itemJob.destinationResidenceId &&
            itemJob.destinationResidenceId === destinationId
          ) {
            items.push(itemJob);
          }
        });

        inventoryValue?.materials?.forEach((material) => {
          if (
            material &&
            material?.serviceBooking?.isItem &&
            material.destinationResidenceId &&
            material.destinationResidenceId === destinationId
          ) {
            const itemJob = {
              itemJob: material,
            } as ItemJobCustomerPortal;

            items.push(itemJob);
          }
        });
      });
      if (items && items.length > 0) {
        destinationItems[`id-${destinationId.toString()}`] = items;
      }
    }
  });

  return destinationItems;
};

export const getCustomDestinationItems = (
  inventory: RoomJobCustomerPortal[],
  destinationList?: Destination[]
) => {
  const destinationsId = destinationList
    ?.filter((destination) => !destination.name?.includes(DESTINATION.NOT_GOING))
    .map((destination) => destination.id);

  const destinationItems: { [x: string]: ItemJobCustomerPortal[] } = {};

  destinationsId?.forEach((destinationId) => {
    if (destinationId) {
      const items: ItemJobCustomerPortal[] = [];

      inventory?.forEach((inventoryValue) => {
        inventoryValue?.itemsJob?.forEach((itemJob) => {
          if (itemJob && itemJob.destinationId === destinationId) {
            items.push(itemJob);
          }
        });
        inventoryValue?.materials?.forEach((material) => {
          if (
            material &&
            material?.serviceBooking?.isItem &&
            material.destinationId &&
            material.destinationId === destinationId
          ) {
            const itemJob = {
              itemJob: material,
            } as ItemJobCustomerPortal;

            items.push(itemJob);
          }
        });
      });

      if (items && items.length > 0) {
        destinationItems[`id-${destinationId.toString()}`] = items;
      }
    }
  });

  return destinationItems;
};

export const getCustomModeDestinationItems = (
  inventory: RoomJobCustomerPortal[],
  modes: JobMode[]
) => {
  const modeDestinationsId = modes
    ?.filter((modeDestination) => modeDestination.isDestination)
    .map((modeDestination) => modeDestination.id);

  const modeDestinationItems: { [x: string]: ItemJobCustomerPortal[] } = {};

  modeDestinationsId?.forEach((modeDestinationId) => {
    if (modeDestinationId) {
      const items: ItemJobCustomerPortal[] = [];

      inventory?.forEach((inventoryValue) => {
        inventoryValue?.itemsJob?.forEach((itemJob) => {
          if (itemJob && itemJob.modeDestinationId === modeDestinationId) {
            items.push(itemJob);
          }
        });
        inventoryValue?.materials?.forEach((material) => {
          if (
            material &&
            material?.serviceBooking?.isItem &&
            material?.modeDestinationId === modeDestinationId
          ) {
            const itemJob = {
              itemJob: material,
            } as ItemJobCustomerPortal;

            items.push(itemJob);
          }
        });
      });

      if (items && items.length > 0) {
        modeDestinationItems[`id-${modeDestinationId.toString()}`] = items;
      }
    }
  });

  return modeDestinationItems;
};

export const calculateByDestination = (
  inventory: RoomJobCustomerPortal[],
  measurementWeight: Measurement,
  mode?: string,
  destination?: string
) => {
  let quantity = 0;
  let weight = 0;

  inventory.forEach((room: RoomJobCustomerPortal) => {
    if (room?.materials && room?.materials?.length > 0) {
      room.materials.forEach((material) => {
        if (material?.serviceBooking?.isItem) {
          if (destination) {
            if (material?.destination?.name?.toLowerCase() === destination?.toLowerCase()) {
              quantity += Number(material?.quantity);
              weight +=
                Number(
                  handleGetConvertValue(
                    Number(material?.weight || 0),
                    measurementWeight?.code || "kg",
                    measurementWeight?.decimalPlaces
                  )
                ) * Number(material?.quantity);
            }
          } else if (mode) {
            if (
              material?.mode?.toLowerCase() === mode?.toLowerCase() &&
              material?.destination?.name?.toLowerCase() !== DESTINATION.NOT_GOING?.toLowerCase()
            ) {
              quantity += Number(material?.quantity);
              weight +=
                Number(
                  handleGetConvertValue(
                    Number(material?.weight || 0),
                    measurementWeight?.code || "kg",
                    measurementWeight?.decimalPlaces
                  )
                ) * Number(material?.quantity);
            }
          }
        }
      });
    }

    if (room?.itemsJob && room?.itemsJob?.length > 0) {
      room.itemsJob.forEach((item: ItemJobCustomerPortal) => {
        if (destination) {
          if (item?.destination?.name?.toLowerCase() === destination?.toLowerCase()) {
            quantity += Number(item?.quantity);
            weight +=
              Number(
                handleGetConvertValue(
                  Number(item?.weight || 0),
                  measurementWeight?.code || "kg",
                  measurementWeight?.decimalPlaces
                )
              ) * Number(item?.quantity);
          }
        } else if (mode) {
          if (
            item.mode?.toLowerCase() === mode?.toLowerCase() &&
            item?.destination?.name?.toLowerCase() !== DESTINATION.NOT_GOING?.toLowerCase()
          ) {
            quantity += Number(item?.quantity);
            weight +=
              Number(
                handleGetConvertValue(
                  Number(item?.weight || 0),
                  measurementWeight?.code || "kg",
                  measurementWeight?.decimalPlaces
                )
              ) * Number(item?.quantity);
          }
        }
      });
    }
  });

  return {
    quantity,
    weight: Number(weight),
  };
};

export const getItemCounts = (
  items: (ItemJobCustomerPortal | MaterialCustomerPortal)[],
  measurementWeight: Measurement
) => {
  let quantity = 0;
  let weight = 0;

  items.forEach((item) => {
    if ("itemJob" in item) {
      quantity += Number(item.itemJob?.quantity) || 0;
    } else {
      quantity += Number(item.quantity) || 0;
    }
    weight += Number(
      handleGetConvertValue(
        Number(item.weight || 0),
        measurementWeight?.code || "kg",
        measurementWeight?.decimalPlaces
      )
    );
  });

  return { quantity, weight };
};
